import { MessageSquare, Mic, MonitorUp, Pen, Video } from 'lucide-react';

/**
 * Stylised preview of the real classroom, drawn in SVG and CSS.
 *
 * Deliberately not a screenshot: an inline illustration stays sharp at every size,
 * needs no asset pipeline, and cannot go stale when the classroom UI changes.
 */
export function ClassroomPreview() {
  return (
    <div className="relative" aria-hidden>
      {/* Soft glow behind the frame to lift it off the page. */}
      <div className="absolute -inset-6 rounded-3xl bg-gradient-to-tr from-brand-200/50 via-brand-100/30 to-transparent blur-2xl" />

      <div className="relative overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl shadow-slate-900/10">
        {/* Classroom chrome */}
        <div className="flex items-center gap-2 bg-slate-800 px-3 py-2.5">
          <span className="text-[11px] font-semibold text-white">Integration</span>
          <span className="rounded-full bg-brand-500/20 px-1.5 py-0.5 text-[9px] font-medium text-brand-200">
            Mathematics
          </span>
          <span className="flex items-center gap-1 rounded-full bg-emerald-500/20 px-1.5 py-0.5 text-[9px] font-medium text-emerald-300">
            <span className="h-1 w-1 rounded-full bg-emerald-400" />
            Live
          </span>

          <div className="ml-auto flex items-center gap-1">
            {[Mic, Video, MonitorUp].map((Icon, index) => (
              <span
                key={index}
                className="flex h-5 w-5 items-center justify-center rounded bg-slate-700 text-slate-300"
              >
                <Icon className="h-2.5 w-2.5" />
              </span>
            ))}
          </div>
        </div>

        <div className="flex">
          {/* Whiteboard */}
          <div className="relative flex-1">
            <div className="flex items-center gap-1.5 border-b border-slate-100 bg-white px-2.5 py-1.5">
              <span className="flex h-5 w-5 items-center justify-center rounded bg-brand-50 text-brand-600">
                <Pen className="h-2.5 w-2.5" />
              </span>
              {['#0f172a', '#dc2626', '#2563eb', '#16a34a'].map((colour) => (
                <span
                  key={colour}
                  className="h-3 w-3 rounded-full ring-1 ring-black/5"
                  style={{ backgroundColor: colour }}
                />
              ))}
            </div>

            <svg viewBox="0 0 320 200" className="block h-auto w-full bg-white" role="presentation">
              <defs>
                <pattern id="preview-grid" width="16" height="16" patternUnits="userSpaceOnUse">
                  <path d="M16 0 L0 0 0 16" fill="none" stroke="#e2e8f0" strokeWidth="1" />
                </pattern>
              </defs>
              <rect width="320" height="200" fill="url(#preview-grid)" />

              {/* A worked integral, as a tutor would sketch it */}
              <text x="24" y="42" fill="#0f172a" fontFamily="Georgia, serif" fontSize="19">
                ∫
              </text>
              <text x="34" y="36" fill="#0f172a" fontFamily="Georgia, serif" fontSize="9">
                3
              </text>
              <text x="34" y="50" fill="#0f172a" fontFamily="Georgia, serif" fontSize="9">
                1
              </text>
              <text x="46" y="42" fill="#0f172a" fontFamily="Georgia, serif" fontSize="15">
                2x dx
              </text>
              <text x="104" y="42" fill="#64748b" fontFamily="Georgia, serif" fontSize="15">
                =
              </text>
              <text x="120" y="42" fill="#2563eb" fontFamily="Georgia, serif" fontSize="15">
                x²
              </text>
              <text x="139" y="36" fill="#2563eb" fontFamily="Georgia, serif" fontSize="9">
                3
              </text>
              <text x="139" y="50" fill="#2563eb" fontFamily="Georgia, serif" fontSize="9">
                1
              </text>
              <text x="152" y="42" fill="#64748b" fontFamily="Georgia, serif" fontSize="15">
                = 8
              </text>

              {/* Curve with the shaded area under it */}
              <path
                d="M40 168 C 90 168, 120 120, 150 96 C 180 72, 220 62, 268 58"
                fill="none"
                stroke="#2563eb"
                strokeWidth="2.5"
                strokeLinecap="round"
              />
              <path
                d="M40 168 C 90 168, 120 120, 150 96 C 180 72, 220 62, 268 58 L268 176 L40 176 Z"
                fill="#2563eb"
                opacity="0.1"
              />
              <line x1="40" y1="176" x2="290" y2="176" stroke="#94a3b8" strokeWidth="1.5" />
              <line x1="40" y1="176" x2="40" y2="62" stroke="#94a3b8" strokeWidth="1.5" />

              {/* Second pen colour — the student's own working */}
              <path
                d="M196 118 q 10 -14 20 0 t 20 0"
                fill="none"
                stroke="#dc2626"
                strokeWidth="2"
                strokeLinecap="round"
              />
              <text x="196" y="146" fill="#dc2626" fontFamily="Georgia, serif" fontSize="11">
                area = 8
              </text>
            </svg>

            {/* Live cursor, as the other participant sees it */}
            <div className="absolute left-[58%] top-[52%]">
              <span className="block h-2.5 w-2.5 rounded-full border-2 border-white bg-brand-500 shadow" />
              <span className="mt-1 inline-block whitespace-nowrap rounded bg-brand-600 px-1.5 py-0.5 text-[8px] font-medium text-white shadow">
                James
              </span>
            </div>

            {/* Video tiles floating over the board */}
            <div className="absolute right-2 top-9 flex flex-col gap-1.5">
              <PreviewTile initials="SW" label="Sarah" tone="bg-brand-500" />
              <PreviewTile initials="JS" label="James" tone="bg-emerald-500" />
            </div>
          </div>

          {/* Chat rail */}
          <div className="hidden w-32 shrink-0 flex-col border-l border-slate-100 bg-white sm:flex">
            <div className="flex items-center gap-1 border-b border-slate-100 px-2 py-1.5">
              <MessageSquare className="h-2.5 w-2.5 text-brand-600" />
              <span className="text-[9px] font-semibold text-brand-700">Chat</span>
            </div>
            <div className="flex-1 space-y-1.5 p-2">
              <span className="inline-block max-w-full rounded-lg rounded-bl-sm bg-slate-100 px-1.5 py-1 text-[8px] leading-snug text-slate-700">
                Try the limits next
              </span>
              <span className="ml-auto block max-w-full rounded-lg rounded-br-sm bg-brand-600 px-1.5 py-1 text-right text-[8px] leading-snug text-white">
                Got 8 — is that right?
              </span>
              <span className="inline-block max-w-full rounded-lg rounded-bl-sm bg-slate-100 px-1.5 py-1 text-[8px] leading-snug text-slate-700">
                Perfect ✓
              </span>
            </div>
          </div>
        </div>

        {/* Page tabs */}
        <div className="flex items-center gap-1.5 border-t border-slate-100 bg-white px-2.5 py-1.5">
          <span className="rounded border border-brand-300 bg-brand-50 px-1.5 py-0.5 text-[8px] font-medium text-brand-700">
            Page 1
          </span>
          <span className="rounded border border-slate-200 px-1.5 py-0.5 text-[8px] font-medium text-slate-500">
            Page 2
          </span>
        </div>
      </div>
    </div>
  );
}

function PreviewTile({ initials, label, tone }: { initials: string; label: string; tone: string }) {
  return (
    <div className="relative h-11 w-16 overflow-hidden rounded-md bg-slate-800 shadow-lg ring-1 ring-black/10">
      <div className="flex h-full items-center justify-center">
        <span className={`flex h-5 w-5 items-center justify-center rounded-full ${tone} text-[8px] font-semibold text-white`}>
          {initials}
        </span>
      </div>
      <span className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent px-1 pb-0.5 text-[7px] font-medium text-white">
        {label}
      </span>
    </div>
  );
}
